/**
 * Scroll locking for a full-screen drawer in an installed iOS PWA.
 *
 * `body { position: fixed }` is the usual iOS scroll lock, but WebKit bug
 * 237961 shows that it corrupts viewport-fit=cover standalone viewports and
 * leaves a system-painted gap. This lock never changes body geometry:
 * background movement is blocked at the touch boundary while scrollable
 * content inside the drawer keeps consuming its own gestures.
 */

export function isIosStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator;
  const ios =
    /iPad|iPhone|iPod/i.test(nav.userAgent) ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (nav as unknown as { standalone?: boolean }).standalone === true;
  return ios && standalone;
}

/**
 * Installed iPhone web apps need an in-flow full-screen surface instead of a
 * fixed drawer. Explicitly exclude both classic and desktop-UA iPads so even
 * an iPad mini keeps the established drawer behavior.
 */
export function isIosStandalonePhonePwa(): boolean {
  if (!isIosStandalonePwa()) return false;
  const nav = window.navigator;
  const ipad =
    /iPad/i.test(nav.userAgent) ||
    nav.platform === "iPad" ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  return !ipad;
}

/**
 * `fingerDelta > 0` means the finger is pulling toward the start (scroll
 * offset decreases); `< 0` means it is pushing toward the end.
 */
export function canConsumeScrollGesture(
  scrollOffset: number,
  clientSize: number,
  scrollSize: number,
  fingerDelta: number,
): boolean {
  const maxOffset = Math.max(0, scrollSize - clientSize);
  if (maxOffset <= 1 || Math.abs(fingerDelta) < 0.5) return false;
  if (fingerDelta > 0) return scrollOffset > 0;
  return scrollOffset < maxOffset - 1;
}

function elementCanConsume(
  element: HTMLElement,
  axis: "x" | "y",
  fingerDelta: number,
): boolean {
  const style = window.getComputedStyle(element);
  const overflow = axis === "x" ? style.overflowX : style.overflowY;
  if (overflow !== "auto" && overflow !== "scroll") return false;
  return axis === "x"
    ? canConsumeScrollGesture(
        element.scrollLeft,
        element.clientWidth,
        element.scrollWidth,
        fingerDelta,
      )
    : canConsumeScrollGesture(
        element.scrollTop,
        element.clientHeight,
        element.scrollHeight,
        fingerDelta,
      );
}

function gestureCanStayInsideDrawer(
  target: Element,
  drawer: HTMLElement,
  axis: "x" | "y",
  fingerDelta: number,
): boolean {
  let element: HTMLElement | null =
    target instanceof HTMLElement ? target : target.parentElement;
  while (element && drawer.contains(element)) {
    if (elementCanConsume(element, axis, fingerDelta)) return true;
    if (element === drawer) break;
    element = element.parentElement;
  }
  return false;
}

export function lockIosStandaloneDrawerScroll(drawer: HTMLElement): () => void {
  const body = document.body;
  const root = document.documentElement;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  const previous = {
    bodyOverflow: body.style.overflow,
    bodyOverscroll: body.style.overscrollBehavior,
    rootOverscroll: root.style.overscrollBehavior,
    lockMarker: body.dataset.traceScrollLock,
  };

  body.style.overflow = "hidden";
  body.style.overscrollBehavior = "none";
  root.style.overscrollBehavior = "none";
  body.dataset.traceScrollLock = "ios-touch-guard";

  let lastX = 0;
  let lastY = 0;

  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;
    lastX = touch.clientX;
    lastY = touch.clientY;
  };

  const onTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    const target = event.target;
    if (!touch || !(target instanceof Element) || !drawer.contains(target)) {
      event.preventDefault();
      return;
    }

    const deltaX = touch.clientX - lastX;
    const deltaY = touch.clientY - lastY;
    lastX = touch.clientX;
    lastY = touch.clientY;

    const horizontal = Math.abs(deltaX) > Math.abs(deltaY);
    const canConsume = gestureCanStayInsideDrawer(
      target,
      drawer,
      horizontal ? "x" : "y",
      horizontal ? deltaX : deltaY,
    );
    if (!canConsume) event.preventDefault();
  };

  document.addEventListener("touchstart", onTouchStart, {
    capture: true,
    passive: true,
  });
  document.addEventListener("touchmove", onTouchMove, {
    capture: true,
    passive: false,
  });

  return () => {
    document.removeEventListener("touchstart", onTouchStart, true);
    document.removeEventListener("touchmove", onTouchMove, true);
    body.style.overflow = previous.bodyOverflow;
    body.style.overscrollBehavior = previous.bodyOverscroll;
    root.style.overscrollBehavior = previous.rootOverscroll;
    if (previous.lockMarker === undefined) {
      delete body.dataset.traceScrollLock;
    } else {
      body.dataset.traceScrollLock = previous.lockMarker;
    }
    window.scrollTo(scrollX, scrollY);
  };
}
